import React, { useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trash2, Loader2 } from "lucide-react";

// Round 20 add-on 2 · D1–D5 — delete confirm for any head-folder row.
// Test-ids: hf-del-dialog hf-del-confirm hf-del-cancel hf-del-unlink hf-del-form hf-del-links
export function HfDeleteButton({ onClick, testid, t }) {
  return (
    <button type="button" title={t("headFolders.del.btn")} aria-label={t("headFolders.del.btn")} data-testid={testid}
            className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-700"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}>
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
}

export function HfDeleteDialog({ target, engId, t, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [links, setLinks] = useState([]);
  const targetRef = target?.ref, targetKind = target?.kind, wsCode = target?.worksheet_code, fsCode = target?.fs_code;
  useEffect(() => {
    setLinks([]);
    if (targetKind === "worksheet" && wsCode) {
      api.post(`/engagements/${engId}/head-folders/${fsCode}/rows/delete`, { kind: "worksheet", worksheet_code: wsCode, check_links: true }, { _silent: true })
        .then((r) => setLinks(r.data?.links || [])).catch(() => {});
    }
  }, [targetRef, targetKind, wsCode, fsCode, engId]);
  if (!target) return null;
  const run = async (mode) => {
    setBusy(true);
    try {
      const body = { kind: target.kind, mode, category: target.category || "", worksheet_code: target.worksheet_code || "",
                     worksheet_kind: target.worksheet_kind || "", form_id: target.form_id || "" };
      await api.post(`/engagements/${engId}/head-folders/${target.fs_code}/rows/delete`, body);
      toast.success(mode === "unlink" ? t("headFolders.del.unlinked") : t("headFolders.del.done"));
      onDone?.(); onClose();
    } catch (e) {
      const code = e?.response?.data?.error_code || e?.response?.data?.code;
      toast.error(code === "HF_DELETE_PARTNER_ONLY" ? t("headFolders.del.partnerOnly") : e?.response?.status === 423 ? t("headFolders.del.locked") : toErrMsg(e, "Failed"));
    } finally { setBusy(false); }
  };
  const isForm = target.kind === "form" && target.form_id;
  const body = target.kind === "standard"
    ? t("headFolders.del.slotBody").replace("{ref}", target.ref)
    : t("headFolders.del.body").replace("{name}", `${target.ref} · ${target.title || ""}`);
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md text-sm" data-testid="hf-del-dialog">
        <DialogHeader><DialogTitle className="text-base">{t("headFolders.del.title")}</DialogTitle></DialogHeader>
        <p data-testid="hf-del-body"><bdi>{body}</bdi></p>
        {links.length > 0 && (
          <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs" data-testid="hf-del-links">
            <div className="font-medium text-amber-900">{t("headFolders.del.linkedWarn")}</div>
            <ul className="list-disc ps-4">{links.map((l) => <li key={l.id}><bdi>{l.label}</bdi> — {l.links.join(", ")}</li>)}</ul>
          </div>
        )}
        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={onClose} data-testid="hf-del-cancel">{t("headFolders.del.cancel")}</Button>
          {isForm ? (<>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => run("unlink")} data-testid="hf-del-unlink">{t("headFolders.del.unlink")}</Button>
            <Button size="sm" className="bg-rose-600 text-white hover:bg-rose-700" disabled={busy} onClick={() => run("delete")} data-testid="hf-del-form">
              {busy ? <Loader2 className="me-1 h-3 w-3 animate-spin" /> : <Trash2 className="me-1 h-3 w-3" />}{t("headFolders.del.deleteForm")}
            </Button>
          </>) : (
            <Button size="sm" className="bg-rose-600 text-white hover:bg-rose-700" disabled={busy} onClick={() => run("delete")} data-testid="hf-del-confirm">
              {busy ? <Loader2 className="me-1 h-3 w-3 animate-spin" /> : <Trash2 className="me-1 h-3 w-3" />}{t("headFolders.del.confirm")}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
